import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
// Replaced react-select with custom headless dropdown for pill-style filters
import { twMerge } from 'tailwind-merge';
import { HealthcareEntitiesService } from '../../../js/api';
import { SessionContext, SessionContextType } from '../../../js/app/contexts/SessionContext';
import { useCodeDefinitions } from '../hooks/useCodeDefinitions';
import { useDebounce } from '../hooks/useDebounce';
import { useWorkflowMetadata } from '../hooks/useWorkflowMetadata';
import type { DateRange } from '../utils/date';
import { getSegmentButtonStateClasses } from '../utils/globalStyles';
import { getUsStateOptions } from '../utils/usStates';
import { BrandSwitch } from './BrandSwitch';
import { Checkbox } from './checkbox';
import { DateRangeFilter } from './date-range-filter';
import { Fieldset, Field, Label } from './fieldset';
import FilterDropdown, { FilterOption } from './filter-dropdown';
import { Input, InputGroup } from './input';

/**
 * Renders Status and Reason filter dropdowns. Only mounted when workflow_scope is provided,
 * so useWorkflowMetadata is only called when we have a valid scope to fetch.
 */
const ListFilterStatusReasonSection: React.FC<{
  workflow_scope: string;
  showStatus: boolean;
  showReason: boolean;
  controlledStatuses: string[];
  controlledReasons: string[];
  onStatusChange: (selected: string[]) => void;
  onReasonChange: (selected: string[]) => void;
  labelOverrides: Record<string, string>;
}> = ({
  workflow_scope,
  showStatus,
  showReason,
  controlledStatuses,
  controlledReasons,
  onStatusChange,
  onReasonChange,
  labelOverrides,
}) => {
  const { statuses } = useWorkflowMetadata(workflow_scope);

  const statusOptions = useMemo(() => {
    if (!statuses) return [];
    return Object.values(statuses)
      .filter((status): status is NonNullable<typeof status> => status !== undefined)
      .map((status) => ({
        value: status.code,
        label: status.title,
        is_active: true,
      }));
  }, [statuses]);

  const reasonOptions = useMemo(() => {
    if (!statuses) return [];
    const reasonsMap = new Map<string, string>();
    Object.values(statuses)
      .filter((status): status is NonNullable<typeof status> => status !== undefined)
      .forEach((status) => {
        status.reasons?.forEach((reason) => {
          if (!reasonsMap.has(reason.code)) {
            reasonsMap.set(reason.code, reason.title);
          }
        });
      });
    return Array.from(reasonsMap.entries())
      .map(([code, title]) => ({
        value: code,
        label: title,
        is_active: true,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [statuses]);

  return (
    <>
      {showStatus && (
        <Field className="tw:flex-none">
          <Label className="tw:sr-only">{labelOverrides['Status'] || 'Status'}</Label>
          <FilterDropdown
            label={labelOverrides['Status'] || 'Status'}
            options={statusOptions}
            value={controlledStatuses}
            multiple
            onChange={(next) => onStatusChange(Array.isArray(next) ? next : [])}
          />
        </Field>
      )}
      {showReason && (
        <Field className="tw:flex-none">
          <Label className="tw:sr-only">{labelOverrides['Reason'] || 'Reason'}</Label>
          <FilterDropdown
            label={labelOverrides['Reason'] || 'Reason'}
            options={reasonOptions}
            value={controlledReasons}
            multiple
            onChange={(next) => onReasonChange(Array.isArray(next) ? next : [])}
          />
        </Field>
      )}
    </>
  );
};

export type DisplayType = 'table' | 'card';

export type BooleanFilterConfig = {
  /**
   * Label shown next to the control when checked / on. Also serves as the
   * single visible label when `renderAs === 'checkbox'`.
   */
  trueLabel: string;
  /**
   * Label shown when unchecked / off. Only rendered in `'switch'` mode.
   */
  falseLabel: string;
  queryString: string;
  /** When param is absent; defaults to true */
  defaultValue?: boolean;
  /**
   * UI control to render this boolean filter as. Defaults to `'switch'`.
   * `'checkbox'` shows a single Checkbox + trueLabel; the off state is just
   * an unchecked box (no falseLabel).
   * `'segmented'` shows a two-option segment `trueLabel | falseLabel` (e.g. Mine | All).
   * A `'switch'` with an empty falseLabel shows just the switch + trueLabel.
   */
  renderAs?: 'switch' | 'checkbox' | 'segmented';
  /**
   * Data scope ("which set am I looking at", e.g. Mine | All, Unassigned) rather than a
   * filter: rendered in the right-hand group left of sort/display, never counted as an
   * active filter, and left untouched by Clear.
   */
  scope?: boolean;
};

/**
 * A page-specific dropdown filter whose options the page supplies (e.g. the
 * facilities of the current org). `key` is both the URL param and the value key
 * in `ListFilterValues.customFilters`; the page maps it to its API param.
 */
export type CustomFilterConfig = {
  key: string;
  label: string;
  options: FilterOption[];
  /** Defaults to true. */
  multiple?: boolean;
  disabled?: boolean;
  /** Tooltip explaining a disabled pill (e.g. "Choose an organization"). */
  disabledHint?: string;
  /** Tooltip on an enabled pill (e.g. that the option set is capped). */
  hint?: string;
};

/** FilterDropdown reports a list (multi) or one value / undefined (single). */
/**
 * A from / to date-range filter the page asks for (e.g. Created, Updated). The picked
 * range lands in `ListFilterValues.dateFilters[key]` as local dates ("YYYY-MM-DD"); the
 * page maps it to its API params (`localDayStartIso` / `localDayEndIso`).
 */
export type DateFilterConfig = {
  key: string;
  label: string;
};

/** The record itself, or undefined when it has no keys (an unset filter group). */
const nonEmpty = <T extends object>(record: T): T | undefined =>
  Object.keys(record).length > 0 ? record : undefined;

const asSelectedList = (next: string[] | string | undefined): string[] => {
  if (Array.isArray(next)) return next;
  return next ? [next] : [];
};

type BooleanFilterControlProps = {
  config: BooleanFilterConfig;
  checked: boolean;
  onChange: (value: boolean) => void;
};

/** Text label that sets the boolean when clicked or activated from the keyboard. */
const ClickableFilterLabel: React.FC<{
  onActivate: () => void;
  ariaLabel?: string;
  children: React.ReactNode;
}> = ({ onActivate, ariaLabel, children }) => (
  <span
    className="tw:text-sm tw:text-zinc-700 tw:cursor-pointer tw:select-none"
    role="button"
    tabIndex={0}
    onClick={onActivate}
    onKeyUp={(e) => {
      if (e.key === ' ' || e.key === 'Enter') onActivate();
    }}
    aria-label={ariaLabel}
  >
    {children}
  </span>
);

/** `trueLabel | falseLabel` segment (e.g. Mine | All). */
const SegmentedBooleanFilter: React.FC<BooleanFilterControlProps> = ({
  config,
  checked,
  onChange,
}) => {
  const options: Array<[boolean, string]> = [
    [true, config.trueLabel],
    [false, config.falseLabel],
  ];
  return (
    <div
      role="group"
      aria-label={`${config.trueLabel} or ${config.falseLabel}`}
      className="tw:flex tw:flex-none tw:items-center tw:border tw:border-zinc-950/10 tw:rounded-lg tw:overflow-hidden"
      data-testid={`list-filter-scope-${config.queryString}`}
    >
      {options.map(([value, label]) => (
        <button
          key={label}
          type="button"
          onClick={() => onChange(value)}
          className={`tw:px-2.5 tw:py-1.5 tw:text-sm tw:transition-colors tw:ease-in-out tw:duration-100 ${getSegmentButtonStateClasses(checked === value)}`}
          aria-pressed={checked === value}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

const CheckboxBooleanFilter: React.FC<BooleanFilterControlProps> = ({
  config,
  checked,
  onChange,
}) => (
  <Field className="tw:flex-none">
    <div className="tw:flex tw:items-center tw:gap-2 tw:px-2">
      <Checkbox
        color="brandgreen"
        checked={checked}
        onChange={(value: boolean) => onChange(value)}
        aria-label={config.trueLabel}
      />
      <ClickableFilterLabel onActivate={() => onChange(!checked)}>
        {config.trueLabel}
      </ClickableFilterLabel>
    </div>
  </Field>
);

/** `falseLabel [switch] trueLabel`; an empty falseLabel shows just the switch + trueLabel. */
const SwitchBooleanFilter: React.FC<BooleanFilterControlProps> = ({
  config,
  checked,
  onChange,
}) => (
  <Field className="tw:flex-none">
    <div className="tw:flex tw:items-center tw:gap-2 tw:px-4">
      {config.falseLabel && (
        <ClickableFilterLabel
          onActivate={() => onChange(false)}
          ariaLabel={`Filter by ${config.falseLabel}`}
        >
          {config.falseLabel}
        </ClickableFilterLabel>
      )}
      <BrandSwitch small checked={checked} onChange={(value) => onChange(value)} />
      <ClickableFilterLabel
        onActivate={() => onChange(true)}
        ariaLabel={`Filter by ${config.trueLabel}`}
      >
        {config.trueLabel}
      </ClickableFilterLabel>
    </div>
  </Field>
);

const BooleanFilterControl: React.FC<BooleanFilterControlProps> = (props) => {
  if (props.config.renderAs === 'segmented') return <SegmentedBooleanFilter {...props} />;
  if (props.config.renderAs === 'checkbox') return <CheckboxBooleanFilter {...props} />;
  return <SwitchBooleanFilter {...props} />;
};

export interface ListFilterValues {
  search?: string;
  statuses?: string[];
  reasons?: string[];
  /** Facility US state codes (e.g. ['TX', 'FL']) */
  states?: string[];
  /** HealthcareEntity UUID */
  healthcare_entity?: string;
  categories?: Record<string, string[]>; // category code -> array of selected values
  /** queryString -> value */
  booleanFilters?: Record<string, boolean>;
  /** custom filter key -> selected values */
  customFilters?: Record<string, string[]>;
  /** key -> picked date range (see `DateFilterConfig`); absent keys are unset. */
  dateFilters?: Record<string, DateRange>;
  displayType?: DisplayType;
  // Ordering string for Django OrderingFilter, e.g. 'updated_at' or '-updated_at'
  ordering?: string;
}

export interface ListFilterProps {
  /**
   * If true, show a text box allowing a user to search (debounced)
   */
  show_search: boolean;
  /**
   * Workflow scope string; used to fetch statuses and reasons for multiselect components
   */
  workflow_scope?: string;
  /**
   * List of code def category strings; for each category, fetch values and populate a multiselect
   * E.g. ['Specialty', 'RateType']
   *
   * NOTE: When using this component, ensure that each category in this array has a corresponding
   * API parameter mapping in the parent component's API call. The parent component should map
   * category names to API parameter names (e.g., 'Specialty' -> 'specialties'). Categories without
   * API mappings will be ignored by the API, which may lead to unexpected filtering behavior.
   */
  categories?: string[];
  /**
   * Boolean that dictates whether the Status multiselect is shown
   */
  showStatus: boolean;
  /**
   * Boolean that dictates whether the Reason multiselect is shown
   */
  showReason: boolean;
  /**
   * Callback function that exposes filter values to parent so parent can construct query params
   */
  onFilterChange: (values: ListFilterValues) => void;
  /**
   * Boolean that dictates if the list display type selector is visible or not
   */
  showDisplayType: boolean;
  /**
   * Boolean that dictates whether the Organization dropdown is shown.
   * The dropdown will only render for staff users.
   */
  showOrganizationFilter?: boolean;
  /**
   * Boolean that dictates whether the US State multiselect is shown. Sends the
   * `state` param; each endpoint decides which state it matches (facility state
   * for opportunities/connections, home state for providers).
   */
  showStateFilter?: boolean;
  /**
   * Optional default display type (defaults to 'table')
   */
  defaultDisplayType?: DisplayType;
  /**
   * Current filter values (controlled component - URL params should be the source of truth)
   */
  values?: Partial<ListFilterValues>;
  /**
   * If true, uses smaller sizing for select components (matches Input/Button small prop)
   */
  // small?: boolean;
  /**
   * Available sort fields. value = API field key (without leading '-')
   */
  sortOptions?: Array<{ label: string; value: string }>;
  /**
   * Optional map to override display labels for filters.
   * Keys should match category names (e.g., 'UrgencyLevel', 'JustificationType')
   * or special keys like 'Status' and 'Reason'. Values are the labels to display.
   */
  labelOverrides?: Record<string, string>;
  /**
   * Boolean filter toggles (e.g. Active/Inactive). Each renders a BrandSwitch with labels.
   * Value is synced to URL under the given queryString.
   */
  booleanFilters?: BooleanFilterConfig[];
  /**
   * Page-specific dropdown filters (options supplied by the page). Each is synced
   * to the URL under its `key` via `useListFilterParams({ customFilters })`.
   */
  customFilters?: CustomFilterConfig[];
  /**
   * Date-range pills (from / to, either end optional), parsed from / written to the URL
   * as `<key>_after` / `<key>_before` via `useListFilterParams({ dateFilters })`.
   */
  dateFilters?: DateFilterConfig[];
}

/**
 * ListFilter Component
 *
 * A reusable controlled component for filtering lists of model objects (Connections, Opportunities, Needs, etc).
 * Provides search, status/reason filtering, category filtering, and display type toggle.
 *
 * URL params should be the single source of truth. This component receives current values from props
 * and calls onFilterChange when user interacts with filters.
 *
 * @example
 * ```tsx
 * const [searchParams, setSearchParams] = useSearchParams();
 * const search = searchParams.get('search') || '';
 * const statuses = searchParams.get('status')?.split(',') || [];
 *
 * <ListFilter
 *   show_search={true}
 *   workflow_scope="staffing.need"
 *   categories={['Specialty', 'RateType']}
 *   showStatus={true}
 *   showReason={true}
 *   showDisplayType={true}
 *   values={{
 *     search,
 *     statuses: statuses.length > 0 ? statuses : undefined,
 *   }}
 *   onFilterChange={(values) => {
 *     // Update URL params from values
 *     const params = new URLSearchParams(searchParams);
 *     if (values.search) params.set('search', values.search);
 *     else params.delete('search');
 *     // ... etc
 *     setSearchParams(params);
 *   }}
 * />
 * ```
 */
export const ListFilter: React.FC<ListFilterProps> = ({
  show_search,
  workflow_scope,
  categories = [],
  showStatus,
  showReason,
  onFilterChange,
  showDisplayType,
  showOrganizationFilter = false,
  showStateFilter = false,
  defaultDisplayType = 'table',
  values = {},
  // small = true,
  sortOptions = [],
  labelOverrides = {},
  booleanFilters = [],
  customFilters = [],
  dateFilters = [],
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { audienceIsStaff, actingAsEntity } = React.useContext<SessionContextType>(SessionContext);

  // Get controlled values from props (URL params are the source of truth)
  // Wrap in useMemo to avoid recreating arrays/objects on every render
  const controlledSearch = values.search || '';
  const controlledStatuses = useMemo(() => values.statuses || [], [values.statuses]);
  const controlledReasons = useMemo(() => values.reasons || [], [values.reasons]);
  const controlledStates = useMemo(() => values.states || [], [values.states]);
  const controlledHealthcareEntity = useMemo(
    () => values.healthcare_entity || '',
    [values.healthcare_entity]
  );
  const controlledCategories = useMemo(() => values.categories || {}, [values.categories]);
  const controlledBooleanFilters = useMemo(
    () => values.booleanFilters || {},
    [values.booleanFilters]
  );
  const controlledCustomFilters = useMemo(() => values.customFilters || {}, [values.customFilters]);
  const controlledDateFilters = useMemo(() => values.dateFilters || {}, [values.dateFilters]);
  // Effective display type: rely on controlled value from parent (resolved in hook)
  const effectiveDisplayType: DisplayType = useMemo(() => {
    return (values.displayType || defaultDisplayType) as DisplayType;
  }, [values.displayType, defaultDisplayType]);

  // Ordering (single source of truth comes from URL via parent hook)
  const controlledOrdering = useMemo(() => values.ordering || '-updated_at', [values.ordering]);
  const currentSortField = controlledOrdering.startsWith('-')
    ? controlledOrdering.slice(1)
    : controlledOrdering;
  const isDesc = controlledOrdering.startsWith('-');

  // Determine if current sort label starts with '#', to decide numeric vs alpha icon
  const currentSortOption = useMemo(() => {
    return sortOptions.find((o) => o.value === currentSortField);
  }, [sortOptions, currentSortField]);
  const isNumericSortLabel = useMemo(() => {
    const label = currentSortOption?.label || '';
    return label.trim().startsWith('#');
  }, [currentSortOption]);

  // Decide if Sort controls should be shown: only in Card view and when options are provided
  const shouldShowSort = useMemo(() => {
    return effectiveDisplayType === 'card' && (sortOptions?.length ?? 0) > 0;
  }, [effectiveDisplayType, sortOptions]);

  // When search is the only filter control (no Status, Reason, or Category dropdowns),
  // let it stretch to fill the space up to the display type toggle
  // Scope booleans live in the right-hand group and are exempt from Clear / active-filter state.
  const filterBooleans = useMemo(
    () => booleanFilters.filter((config) => !config.scope),
    [booleanFilters]
  );
  const scopeBooleans = useMemo(
    () => booleanFilters.filter((config) => config.scope),
    [booleanFilters]
  );

  // Staff-only; hidden while acting as an organization — the acting-as pill is
  // the indicator and `useListFilterParams` locks the value to that org.
  const shouldShowOrganizationFilter = useMemo(() => {
    return showOrganizationFilter && Boolean(audienceIsStaff) && !actingAsEntity;
  }, [showOrganizationFilter, audienceIsStaff, actingAsEntity]);

  const isSearchOnlyFilter = useMemo(() => {
    return (
      show_search &&
      !showStatus &&
      !showReason &&
      !shouldShowOrganizationFilter &&
      (categories?.length ?? 0) === 0 &&
      filterBooleans.length === 0 &&
      customFilters.length === 0 &&
      dateFilters.length === 0
    );
  }, [
    show_search,
    showStatus,
    showReason,
    shouldShowOrganizationFilter,
    categories,
    filterBooleans,
    customFilters,
    dateFilters,
  ]);

  // Local state for search input (for debouncing - we don't want to update URL on every keystroke)
  // Sync with the controlled value when it changes externally (e.g., from URL)
  const [searchInput, setSearchInput] = useState(controlledSearch);

  // Sync search input when controlled value changes externally (e.g., browser back/forward)
  useEffect(() => {
    setSearchInput(controlledSearch);
  }, [controlledSearch]);

  const debouncedSearch = useDebounce(searchInput, 300);

  const { data: codeDefinitionsData } = useCodeDefinitions();

  const { data: healthcareEntitiesData } = useQuery({
    queryKey: ['healthcare-entities', 'list-filter'],
    queryFn: async () => {
      // Fetch a large batch; dropdown is only intended for staff use.
      return HealthcareEntitiesService.healthcareEntitiesList({ limit: 250, offset: 0 });
    },
    enabled: shouldShowOrganizationFilter,
    staleTime: 5 * 60 * 1000,
  });

  const healthcareEntityOptions = useMemo(() => {
    const results = healthcareEntitiesData?.results ?? [];
    return results
      .filter((e): e is NonNullable<typeof e> => e !== undefined && e !== null)
      .map((e) => ({
        value: e.id,
        label: e.name,
        is_active: true,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [healthcareEntitiesData]);

  // No longer using react-select styles; FilterDropdown is tailwind-based

  const categoryOptionsMap = useMemo(() => {
    const optionsMap: Record<
      string,
      Array<{ value: string; label: string; is_active?: boolean }>
    > = {};
    if (!codeDefinitionsData) return optionsMap;

    categories.forEach((category) => {
      const categoryDefs = codeDefinitionsData.byCategory.get(category) || [];
      optionsMap[category] = categoryDefs.map((def) => ({
        value: def.code,
        label: def.title,
        is_active: def.is_active,
      }));
    });

    return optionsMap;
  }, [codeDefinitionsData, categories]);

  const stateOptions = useMemo(
    () => getUsStateOptions().map((o) => ({ ...o, is_active: true })),
    []
  );

  // Every change re-emits the full value set with one field replaced.
  const currentValues = (): ListFilterValues => ({
    search: debouncedSearch || undefined,
    statuses: controlledStatuses.length > 0 ? controlledStatuses : undefined,
    reasons: controlledReasons.length > 0 ? controlledReasons : undefined,
    states: controlledStates.length > 0 ? controlledStates : undefined,
    healthcare_entity: controlledHealthcareEntity || undefined,
    categories: Object.keys(controlledCategories).length > 0 ? controlledCategories : undefined,
    booleanFilters:
      Object.keys(controlledBooleanFilters).length > 0 ? controlledBooleanFilters : undefined,
    customFilters:
      Object.keys(controlledCustomFilters).length > 0 ? controlledCustomFilters : undefined,
    dateFilters: nonEmpty(controlledDateFilters),
    displayType: effectiveDisplayType,
    ordering: controlledOrdering,
  });
  const emit = (patch: Partial<ListFilterValues>) =>
    onFilterChange({ ...currentValues(), ...patch });

  // Track previous debounced search to detect changes
  const prevDebouncedSearchRef = useRef<string>(debouncedSearch);

  // Notify parent when debounced search changes
  // Include controlled values in dependencies so we use latest values when search changes
  useEffect(() => {
    if (prevDebouncedSearchRef.current !== debouncedSearch) {
      prevDebouncedSearchRef.current = debouncedSearch;
      onFilterChange({
        search: debouncedSearch || undefined,
        statuses: controlledStatuses.length > 0 ? controlledStatuses : undefined,
        reasons: controlledReasons.length > 0 ? controlledReasons : undefined,
        states: controlledStates.length > 0 ? controlledStates : undefined,
        healthcare_entity: controlledHealthcareEntity || undefined,
        categories: Object.keys(controlledCategories).length > 0 ? controlledCategories : undefined,
        booleanFilters:
          Object.keys(controlledBooleanFilters).length > 0 ? controlledBooleanFilters : undefined,
        customFilters:
          Object.keys(controlledCustomFilters).length > 0 ? controlledCustomFilters : undefined,
        dateFilters: nonEmpty(controlledDateFilters),
        displayType: effectiveDisplayType,
        ordering: controlledOrdering,
      });
    }
  }, [
    debouncedSearch,
    controlledStatuses,
    controlledReasons,
    controlledStates,
    controlledHealthcareEntity,
    controlledCategories,
    controlledBooleanFilters,
    controlledCustomFilters,
    controlledDateFilters,
    effectiveDisplayType,
    controlledOrdering,
    onFilterChange,
  ]);

  const handleStatusChange = (selected: string[]) =>
    emit({ statuses: selected.length > 0 ? selected : undefined });

  const handleReasonChange = (selected: string[]) =>
    emit({ reasons: selected.length > 0 ? selected : undefined });

  const handleStateChange = (selected: string[]) =>
    emit({ states: selected.length > 0 ? selected : undefined });

  const handleCategoryChange = (category: string, selected: string[]) => {
    const updatedCategories = { ...controlledCategories };
    if (selected.length === 0) {
      delete updatedCategories[category];
    } else {
      updatedCategories[category] = selected;
    }
    emit({
      categories: Object.keys(updatedCategories).length > 0 ? updatedCategories : undefined,
    });
  };

  const handleBooleanFilterChange = (queryString: string, value: boolean) =>
    emit({ booleanFilters: { ...controlledBooleanFilters, [queryString]: value } });

  const handleCustomFilterChange = (key: string, selected: string[]) => {
    const updated = { ...controlledCustomFilters };
    if (selected.length === 0) {
      delete updated[key];
    } else {
      updated[key] = selected;
    }
    emit({ customFilters: Object.keys(updated).length > 0 ? updated : undefined });
  };

  const handleDateFilterChange = (key: string, range: DateRange | undefined) => {
    const updated = { ...controlledDateFilters };
    if (range) {
      updated[key] = range;
    } else {
      delete updated[key];
    }
    emit({ dateFilters: nonEmpty(updated) });
  };

  const handleHealthcareEntityChange = (next?: string) =>
    emit({ healthcare_entity: next && next.length > 0 ? next : undefined });

  const handleDisplayTypeChange = (newDisplayType: DisplayType) =>
    emit({ displayType: newDisplayType });

  // Determine if any filters/search are currently active to show Clear Filters button
  const hasActiveFilters = useMemo(() => {
    const hasSearch = (searchInput || '').trim().length > 0;
    const hasStatuses = controlledStatuses.length > 0;
    const hasReasons = controlledReasons.length > 0;
    const hasStates = controlledStates.length > 0;
    // The locked (acting-as) org is not a user-chosen filter.
    const hasHealthcareEntity =
      shouldShowOrganizationFilter && (controlledHealthcareEntity || '').trim().length > 0;
    const hasCategories = Object.keys(controlledCategories).length > 0;
    const hasCustomFilters = Object.values(controlledCustomFilters).some(
      (selected) => selected.length > 0
    );
    const hasDateFilters = Object.keys(controlledDateFilters).length > 0;
    const hasBooleanFiltersDiffFromDefault = filterBooleans.some(
      (config) =>
        (controlledBooleanFilters[config.queryString] ?? config.defaultValue ?? true) !==
        (config.defaultValue ?? true)
    );
    return (
      hasSearch ||
      hasStatuses ||
      hasReasons ||
      hasStates ||
      hasHealthcareEntity ||
      hasCategories ||
      hasCustomFilters ||
      hasDateFilters ||
      hasBooleanFiltersDiffFromDefault
    );
  }, [
    searchInput,
    controlledStatuses,
    controlledReasons,
    controlledStates,
    controlledHealthcareEntity,
    shouldShowOrganizationFilter,
    controlledCategories,
    controlledCustomFilters,
    controlledDateFilters,
    controlledBooleanFilters,
    filterBooleans,
  ]);

  const handleClearAll = () => {
    // Scope choices (Mine | All, Unassigned) survive Clear; only real filters reset.
    const keptScope = Object.fromEntries(
      scopeBooleans
        .filter((config) => config.queryString in controlledBooleanFilters)
        .map((config) => [config.queryString, controlledBooleanFilters[config.queryString]])
    );
    // Clear local search input immediately
    setSearchInput('');
    // Notify parent that all filters are cleared; preserve display type and ordering
    emit({
      search: undefined,
      statuses: undefined,
      reasons: undefined,
      states: undefined,
      healthcare_entity: undefined,
      categories: undefined,
      booleanFilters: Object.keys(keptScope).length > 0 ? keptScope : undefined,
      customFilters: undefined,
      dateFilters: undefined,
    });
  };

  // Sort handlers
  const handleSortFieldChange = (nextField?: string) => {
    // If cleared, reset to default '-updated_at' (descending)
    if (!nextField || nextField.length === 0) {
      emit({ ordering: '-updated_at' });
      return;
    }
    emit({ ordering: isDesc ? `-${nextField}` : nextField });
  };

  const handleToggleSortDirection = () =>
    emit({ ordering: isDesc ? currentSortField : `-${currentSortField}` });

  const renderBooleanFilter = (config: BooleanFilterConfig) => (
    <BooleanFilterControl
      key={config.queryString}
      config={config}
      checked={controlledBooleanFilters[config.queryString] ?? config.defaultValue ?? true}
      onChange={(value) => handleBooleanFilterChange(config.queryString, value)}
    />
  );

  return (
    <div id="list-filter-section" className=" tw:py-1">
      {/* One-row layout (desktop) with wrapping on small screens */}
      <Fieldset
        className={twMerge(
          'tw:flex tw:flex-wrap tw:items-center tw:gap-1 tw:mb-2',
          isSearchOnlyFilter && 'tw:md:flex-nowrap'
        )}
      >
        {/* Search Box */}
        {show_search && (
          <Field className="tw:min-w-0 tw:basis-full tw:w-full tw:flex-[1_1_100%] tw:md:flex-[0_1_auto] tw:md:grow-0 tw:md:shrink tw:md:!w-auto">
            <InputGroup className="tw:!min-w-0 tw:md:!min-w-0">
              <i className="fas fa-magnifying-glass" data-slot="icon" />
              <Input
                id="list-filter-search-input"
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search..."
                className="tw:!min-w-0 tw:ml:!min-w-0"
                data-testid="list-filter-search"
              />
            </InputGroup>
          </Field>
        )}

        {/* Status and Reason filters - only mount when workflow_scope exists to avoid fetching with empty scope */}
        {(showStatus || showReason) && workflow_scope && (
          <ListFilterStatusReasonSection
            workflow_scope={workflow_scope}
            showStatus={showStatus}
            showReason={showReason}
            controlledStatuses={controlledStatuses}
            controlledReasons={controlledReasons}
            onStatusChange={handleStatusChange}
            onReasonChange={handleReasonChange}
            labelOverrides={labelOverrides}
          />
        )}

        {/* Organization Filter (staff-only) */}
        {shouldShowOrganizationFilter && (
          <Field className="tw:flex-none" data-testid="list-filter-organization">
            <Label className="tw:sr-only">{labelOverrides['Organization'] || 'Organization'}</Label>
            <FilterDropdown
              label={labelOverrides['Organization'] || 'Organization'}
              options={healthcareEntityOptions}
              value={controlledHealthcareEntity}
              multiple={false}
              onChange={(next) =>
                handleHealthcareEntityChange(typeof next === 'string' ? next : undefined)
              }
            />
          </Field>
        )}

        {/* State Filter (`state` param; the endpoint picks facility vs home state) */}
        {showStateFilter && (
          <Field className="tw:flex-none" data-testid="list-filter-state">
            <Label className="tw:sr-only">{labelOverrides['State'] || 'State'}</Label>
            <FilterDropdown
              label={labelOverrides['State'] || 'State'}
              options={stateOptions}
              value={controlledStates}
              multiple
              onChange={(next) => handleStateChange(Array.isArray(next) ? next : [])}
            />
          </Field>
        )}

        {/* Category Filters */}
        {categories.map((category) => {
          const categoryOptions = categoryOptionsMap[category] || [];
          const selectedCategoryValues = controlledCategories[category] || [];

          return (
            <Field key={category} className="tw:flex-none">
              <Label className="tw:sr-only">{labelOverrides[category] || category}</Label>
              <FilterDropdown
                label={labelOverrides[category] || category}
                options={categoryOptions}
                value={selectedCategoryValues}
                multiple
                // Let the pill size to content in the flex row
                onChange={(next) => handleCategoryChange(category, Array.isArray(next) ? next : [])}
              />
            </Field>
          );
        })}

        {/* Custom (page-supplied) dropdown filters */}
        {customFilters.map((config) => (
          <Field
            key={config.key}
            className="tw:flex-none"
            title={config.disabled ? config.disabledHint : config.hint}
            data-testid={`list-filter-custom-${config.key}`}
          >
            <Label className="tw:sr-only">{config.label}</Label>
            <FilterDropdown
              label={config.label}
              options={config.options}
              value={controlledCustomFilters[config.key] || []}
              multiple={config.multiple ?? true}
              disabled={config.disabled}
              onChange={(next) => handleCustomFilterChange(config.key, asSelectedList(next))}
            />
          </Field>
        ))}

        {/* Date-range filters (from / to), one pill per date the page asks for */}
        {dateFilters.map((config) => (
          <div key={config.key} className="tw:flex-none">
            <DateRangeFilter
              label={config.label}
              value={controlledDateFilters[config.key]}
              onChange={(range) => handleDateFilterChange(config.key, range)}
              testId={`list-filter-date-${config.key}`}
            />
          </div>
        ))}

        {/* Boolean Filters (e.g. Active/Inactive toggle, or single checkbox) */}
        {filterBooleans.map(renderBooleanFilter)}

        {/* Clear Filters button moved to the left group (right-most among left controls) */}
        {hasActiveFilters && (
          <div className="tw:flex-none">
            <button
              type="button"
              onClick={handleClearAll}
              className={twMerge(
                'tw:inline-flex tw:items-center tw:gap-1 tw:!rounded-lg tw:border tw:border-zinc-950/10 ',
                'tw:px-2.5 tw:py-1.5 tw:text-sm tw:text-zinc-700 tw:hover:bg-zinc-50',
                'tw:ml-auto tw:md:ml-0',
                'tw:!text-brand-green-0 tw:!border-brand-green-0 tw:bg-white ',
                'tw:data-[active]:bg-brand-green-0/5 tw:data-[hover]:bg-brand-green-0/5'
              )}
              aria-label="Clear all filters"
              title="Clear all filters"
            >
              <i className="fas fa-times tw:text-brand-green-0" />
              <span>Clear</span>
            </button>
          </div>
        )}

        {/* Display Type Toggle and Sort (right-aligned on md+, left-aligned when wrapped/small) */}
        {(showDisplayType || shouldShowSort || scopeBooleans.length > 0) && (
          <div
            className={twMerge(
              'tw:flex tw:items-center tw:gap-1 tw:flex-shrink-0',
              // When search is the only filter, keep display toggle on same row (no basis-full)
              isSearchOnlyFilter
                ? 'tw:mt-0 tw:ml-auto tw:basis-auto tw:w-auto tw:justify-end'
                : 'tw:mt-2 tw:ml-0 tw:basis-full tw:w-full tw:justify-start tw:group-data-[drawer-open=true]:mt-2 tw:group-data-[drawer-open=true]:ml-0 tw:group-data-[drawer-open=true]:basis-full tw:group-data-[drawer-open=true]:w-full tw:group-data-[drawer-open=true]:justify-start tw:md:mt-0 tw:md:ml-auto tw:md:basis-auto tw:md:w-auto'
            )}
          >
            {/* Data scope (e.g. Mine | All, Unassigned): what set is shown; untouched by Clear */}
            {scopeBooleans.length > 0 && (
              <div
                className="tw:flex tw:items-center tw:gap-1 tw:mr-2"
                data-testid="list-filter-scope"
              >
                {scopeBooleans.map(renderBooleanFilter)}
              </div>
            )}

            {/* Sort controls (card & table use this as source of truth) */}
            {shouldShowSort && sortOptions.length > 0 && (
              <>
                <FilterDropdown
                  label="Sort"
                  labelOverride="Sort List By"
                  options={sortOptions.map((o) => ({
                    value: o.value,
                    label: o.label,
                    is_active: true,
                  }))}
                  pillTextClassName="tw:!text-brand-green-1"
                  value={currentSortField}
                  multiple={false}
                  onChange={(next) =>
                    handleSortFieldChange(typeof next === 'string' ? next : undefined)
                  }
                />
                <button
                  type="button"
                  onClick={handleToggleSortDirection}
                  className="tw:inline-flex tw:items-center tw:gap-1 tw:!rounded-lg tw:border tw:border-zinc-950/10 tw:bg-white tw:px-2 tw:py-1.5  tw:text-zinc-700 tw:hover:bg-zinc-50"
                  aria-label={isDesc ? 'Sort descending' : 'Sort ascending'}
                  title={isDesc ? 'Descending' : 'Ascending'}
                >
                  <i
                    className={`fas ${
                      isNumericSortLabel
                        ? isDesc
                          ? 'fa-arrow-down-9-1'
                          : 'fa-arrow-down-1-9'
                        : isDesc
                          ? 'fa-arrow-down-z-a'
                          : 'fa-arrow-down-a-z'
                    } tw:text-brand-green-1 tw:w-5 tw:h-5`}
                  />
                </button>
              </>
            )}

            {showDisplayType && (
              <div
                className="tw:flex tw:items-center tw:gap-1 tw:border tw:border-zinc-950/10 tw:rounded-lg tw:overflow-hidden"
                data-testid="list-filter-display-type"
              >
                <button
                  type="button"
                  onClick={() => handleDisplayTypeChange('table')}
                  className={`tw:px-2 tw:py-1.5 tw:transition-colors tw:ease-in-out tw:duration-100 ${getSegmentButtonStateClasses(effectiveDisplayType === 'table')}`}
                  aria-pressed={effectiveDisplayType === 'table'}
                  aria-label="Table view"
                >
                  <i className="fas fa-bars tw:w-5 tw:h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDisplayTypeChange('card')}
                  className={`tw:px-2 tw:py-1.5 tw:transition-colors tw:ease-in-out tw:duration-100 ${getSegmentButtonStateClasses(effectiveDisplayType === 'card')}`}
                  aria-pressed={effectiveDisplayType === 'card'}
                  aria-label="Card view"
                >
                  <i className="fas fa-grid tw:w-5 tw:h-5" />
                </button>
              </div>
            )}
          </div>
        )}
      </Fieldset>
    </div>
  );
};
